import type { VehicleState } from './types';

/**
 * The PURE vehicle-state ordering + counting helpers, extracted from
 * web/src/features/live/vehicleState.ts (slice 84) so the customer map and the
 * staff Global-live list order and count a fleet identically. The i18n-coupled
 * parts of vehicleState (buildStatusLine, relativeTime, chips) stay in web — only
 * the dependency-free rank/order/count/sort move here. web re-exports these.
 */

// Rank for counts, chips and list ordering: the actionable states first. A fleet
// manager scans the top. `engineOn` sits second — right after moving — because it
// is the one that most wants a look; `engineStale` next (last seen DRIVING, now
// quiet); then idling/parked; then the states that carry no observation at all.
const STATE_RANK: Record<VehicleState, number> = {
  moving: 0,
  engineOn: 1,
  engineStale: 2,
  idling: 3,
  parked: 4,
  noContact: 5,
  unknown: 6,
};

/** Every state, in rank order — derived from `STATE_RANK`, so it can never drift
 *  from the union. Drives the chips, the fleet summary and the list order. */
export const VEHICLE_STATE_ORDER: VehicleState[] = (
  Object.keys(STATE_RANK) as VehicleState[]
).sort((a, b) => STATE_RANK[a] - STATE_RANK[b]);

export type StateCounts = Record<VehicleState, number>;

/** One count per state, initialised from the exhaustive order so a new state gets
 *  its own bucket automatically (starting at 0) rather than vanishing. */
export function countByState(vehicles: readonly { state: VehicleState }[]): StateCounts {
  const counts = Object.fromEntries(VEHICLE_STATE_ORDER.map((s) => [s, 0])) as StateCounts;
  for (const v of vehicles) counts[v.state] += 1;
  return counts;
}

/** List order: by state rank, then freshest position first — the same shape the
 *  old motion-based sort had, now keyed on the vehicle-state vocabulary. */
export function sortByState<T extends { state: VehicleState; positionAgeSec: number | null }>(
  vehicles: readonly T[],
): T[] {
  return [...vehicles].sort((a, b) => {
    const byState = STATE_RANK[a.state] - STATE_RANK[b.state];
    if (byState !== 0) return byState;
    const ageA = a.positionAgeSec ?? Number.POSITIVE_INFINITY;
    const ageB = b.positionAgeSec ?? Number.POSITIVE_INFINITY;
    return ageA - ageB;
  });
}
